//
// # CommandPaletteDialog
//
// Shared scaffolding for the command-palette-style dialogs (anchored at the top
// of the screen) — see IDE AGENTS.md: dialogs use the command-palette style, not
// the floating Modal. Renders the backdrop, dialog frame, title, a body slot
// (children) and a footer with the Enter/Escape hint plus Cancel/Submit
// buttons. Callers own their form state and validation and pass in canSubmit,
// the submit label and the submit/close handlers.
//

import React, { useCallback } from "react";
import { createPortal } from "react-dom";

export interface CommandPaletteDialogProps {
  // Title shown at the top of the dialog.
  readonly title: string;
  // Footer hint, e.g. "Enter to create · Escape to cancel".
  readonly hint: string;
  // Submit button label; callers vary it for the in-flight state (e.g.
  // "Create" vs "Creating...").
  readonly submitLabel: string;
  // Controls only the Submit button's disabled state.
  readonly canSubmit: boolean;
  // A submit *attempt*: fired by the Submit button and by Enter. It runs even
  // when !canSubmit (Enter is deliberately not gated) so the handler can mark
  // fields touched and surface validation errors. The handler MUST therefore
  // guard on validity itself before performing the action — do not assume
  // canSubmit was true when this fires.
  readonly onSubmit: () => void;
  readonly onClose: () => void;
  // Dialog body — the form fields.
  readonly children: React.ReactNode;
}

export function CommandPaletteDialog({
  title,
  hint,
  submitLabel,
  canSubmit,
  onSubmit,
  onClose,
  children,
}: CommandPaletteDialogProps) {
  // Submit on Enter, cancel on Escape — the command-palette convention. Skip
  // Enter when a button has focus, since Enter there activates that button
  // (e.g. Cancel, or a type toggle) rather than submitting the form. Enter is
  // intentionally not gated on canSubmit: onSubmit is an attempt that lets the
  // handler reveal validation errors (see onSubmit above).
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (
        e.key === "Enter" &&
        !(e.target instanceof HTMLButtonElement)
      ) {
        e.preventDefault();
        onSubmit();
      }
    },
    [onSubmit, onClose]
  );

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  return createPortal(
    <div
      className="command-palette-backdrop"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
    >
      <div className="command-palette add-instance-dialog" role="dialog">
        <div className="command-palette-prompt-label">{title}</div>
        <div className="add-instance-params">{children}</div>
        <div className="add-instance-footer">
          <span className="add-instance-hint">{hint}</span>
          <div className="flex gap-2">
            <button
              className="add-instance-btn"
              style={{
                background: "transparent",
                color: "var(--color-ide-text-muted)",
              }}
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className="add-instance-btn"
              onClick={onSubmit}
              disabled={!canSubmit}
            >
              {submitLabel}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
